import * as Sentry from '@sentry/vue'
import type { App } from 'vue'
import type { Router } from 'vue-router'
import { isSentryEnabled, sentryDsn, appEnvironment } from './env'

export const CLERK_NETWORK_ERROR_RE = /^ClerkJS: Network error at /

/**
 * Initialize Sentry error tracking.
 * No-op when VITE_SENTRY_DSN is empty or an unsubstituted RUNTIME_ placeholder.
 * Call immediately after createApp(), before app.mount().
 */
export function initSentry(app: App, router: Router): void {
  if (!isSentryEnabled) return

  Sentry.init({
    app,
    dsn: sentryDsn,
    environment: appEnvironment,
    release: import.meta.env.VITE_SENTRY_RELEASE || undefined,
    sendDefaultPii: false,

    // Performance: lean rate — trace-stitching insurance only, not perf data (P1).
    // Frontend + backend share the same org quota; backend is the dominant consumer.
    tracesSampleRate: 0.05,

    // Replay: only record sessions that hit an error (free tier: 50 replays/mo).
    // Default masking: all text masked, all media blocked.
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,

    integrations: [
      Sentry.browserTracingIntegration({ router }),
      Sentry.replayIntegration(),
    ],

    // Noise filter: browser-extension errors and known benign exceptions.
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      CLERK_NETWORK_ERROR_RE,
    ],
    denyUrls: [
      /extensions\//i,
      /^chrome-extension:\/\//i,
      /^moz-extension:\/\//i,
    ],

    // NOTE: tracePropagationTargets intentionally omitted until Phase 6.
    // Enabling it before the backend allows sentry-trace/baggage headers in
    // CORS would break sampled cross-origin API calls. See spec §Phase 6.
  })
}

/**
 * Associate the current Sentry scope with a user ID (Clerk).
 * Pass null to clear (e.g. on sign-out).
 * Only Clerk user ID — no email, no IP (sendDefaultPii is false).
 */
export function setSentryUser(id: string | null): void {
  if (!isSentryEnabled) return
  Sentry.setUser(id ? { id } : null)
}

/**
 * Replace numeric and UUID path segments with :id for fingerprint grouping.
 * e.g. /review/123 → /review/:id
 *      /api/users/abc-def-123/posts/456 → /api/users/:id/posts/:id
 */
export function normalizeEndpoint(path: string): string {
  return path.replace(
    /\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|\/\d+/gi,
    '/:id',
  )
}

/**
 * Capture a Clerk UI load timeout — fired when waitForClerk() exceeds its deadline.
 * Tracked as a warning so we get frequency data on blank-screen incidents.
 */
export function captureClerkLoadTimeout(destination: string): void {
  if (!isSentryEnabled) return
  Sentry.captureMessage('Clerk UI failed to load within timeout', {
    level: 'warning',
    tags: { 'clerk.error': 'load_timeout', 'nav.to': destination },
  })
}

/**
 * Capture an API error (5xx or network failure) to Sentry with
 * endpoint/status tags and a fingerprint for grouping.
 * Skip 401 and other 4xx — those are expected app flow.
 */
export function captureApiError(
  error: unknown,
  context: { endpoint: string; status?: number },
): void {
  if (!isSentryEnabled) return

  const normalized = normalizeEndpoint(context.endpoint)

  Sentry.captureException(error, {
    fingerprint: ['api-error', String(context.status ?? 'network'), normalized],
    tags: {
      'api.endpoint': normalized,
      'api.status': context.status ? String(context.status) : 'network',
      'error.source': 'api-response',
    },
  })
}
